import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { observer } from "mobx-react-lite";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { buildDataExportWorkbook, ExportBlock } from "src/utils/dataExportWorkbook";
import { useServices } from "src/services";
import { useStores } from "src/stores";
import { D4URxEvent } from "src/utils/classes";
import { FUNDING_PURPOSE_CATEGORIES, ORGANIZATION_TYPES, ORGANIZATION_SUBCATEGORIES, FBT_PARTNERS } from "src/utils/approvalOptions";
import {
  APPROVED_STATUSES,
  EVENT_IDS_EXCLUDED_FROM_STATS,
  DONATION_PER_DRIVE,
  MAX_DRIVES_PER_EVENT,
  MAX_DONATION_PER_EVENT,
} from "src/utils/eventStats";

// Data tab. Every number is computed from the same season data as the Events tab (getEvents),
// with the same "approved" statuses and stats exclusions, so the two pages always agree.
// The reapply numbers come from event_requests history (data/reapplications).

// Series colors, checked with the dataviz palette validator against this page's light surface.
// UNCLASSIFIED is a neutral for "no data yet", not a series.
const COLORS = {
  school: "#1d6ce0",
  community: "#c2550c",
  unclassified: "#d5d8dd",
  bar: "#1d6ce0",
  track: "#e7e9ed",
};


interface Reapplications {
  reapplied: number;
  approved: number;
  declined_again: number;
  pending: number;
}

const pct = (part: number, whole: number) => (whole ? `${((part / whole) * 100).toFixed(1)}%` : "0.0%");
const money = (n: number) => `$${Math.round(n).toLocaleString("en-US")}`;
// averages show one decimal; "N/A" when nothing qualifies yet
const oneDecimal = (n: number | null) => (n === null ? "N/A" : n.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }));

const fundingCategoryOf = (event: D4URxEvent): string => {
  try {
    const spreadsheet = JSON.parse(event.spreadsheet_data || "{}");
    return (spreadsheet.funding_purpose_category || event.funding_purpose_category || "").trim();
  } catch (e) {
    return (event.funding_purpose_category || "").trim();
  }
};

const DataScreen = () => {
  const { d4ur } = useServices();
  const { ui, login } = useStores();
  const exportRef = useRef<ExcelExport | null>(null);

  const [seasons, setSeasons] = useState<any[]>([]);
  const [seasonId, setSeasonId] = useState<number>(ui.currentSeason.season_id);
  const [events, setEvents] = useState<D4URxEvent[]>([]);
  const [reapplications, setReapplications] = useState<Reapplications | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);

  useEffect(() => {
    d4ur.getSeasons().then((ret: any) => {
      if (Array.isArray(ret?.result)) setSeasons(ret.result);
    });
  }, [d4ur]);

  useEffect(() => {
    setLoading(true);
    setError("");
    Promise.all([ d4ur.getEvents(seasonId), d4ur.getReapplications(seasonId) ])
      .then(([ eventsRet, reapplyRet ]: any[]) => {
        if (!Array.isArray(eventsRet?.result)) throw new Error(eventsRet?.result?.message || "Failed to load events");
        setEvents(eventsRet.result.map((e: any) => new D4URxEvent(e)));
        setReapplications(reapplyRet?.response?.status === 200 ? reapplyRet.result : null);
        setUpdatedAt(new Date());
      })
      .catch((e) => setError(e.message || "Failed to load data"))
      .finally(() => setLoading(false));
  }, [d4ur, seasonId]);

  const seasonName = seasons.find((s) => s.season_id === seasonId)?.season_name
    || (seasonId === ui.currentSeason.season_id ? ui.currentSeason.season_name : String(seasonId));

  const stats = useMemo(() => {
    const all = events.filter((e) => !EVENT_IDS_EXCLUDED_FROM_STATS.includes(e.event_id as number));
    const isApproved = (e: D4URxEvent) => APPROVED_STATUSES.includes(e.status as string);
    const approved = all.filter(isApproved);

    // Same split as the old Events header cards: first-time excludes Hispanic dealers
    const firstTime = all.filter((e) => e.first_time_dealer && !e.hispanic);
    const hispanic = all.filter((e) => e.hispanic);

    const fundingCounts = new Map<string, number>();
    approved.forEach((e) => {
      const category = fundingCategoryOf(e) || "Not specified";
      fundingCounts.set(category, (fundingCounts.get(category) || 0) + 1);
    });
    const funding = [
      ...FUNDING_PURPOSE_CATEGORIES.map((category) => ({ category, count: fundingCounts.get(category) || 0 })),
      ...Array.from(fundingCounts.entries())
        .filter(([ category ]) => !FUNDING_PURPOSE_CATEGORIES.includes(category))
        .map(([ category, count ]) => ({ category, count })),
    ];

    // FBT partner (events.fbt_partner) from the Approve Event dialog, as a share of all approved
    // events. Not the dealer's own pick (nat_charity). A stored value no longer offered gets its own row.
    const fbtCounts = new Map<string, number>();
    approved.forEach((e) => {
      if (e.fbt_partner) fbtCounts.set(e.fbt_partner, (fbtCounts.get(e.fbt_partner) || 0) + 1);
    });
    const fbt = [
      ...FBT_PARTNERS.map((partner) => ({ partner, count: fbtCounts.get(partner) || 0 })),
      ...Array.from(fbtCounts.entries())
        .filter(([ partner ]) => !FBT_PARTNERS.includes(partner))
        .map(([ partner, count ]) => ({ partner, count })),
    ];
    const fbtTotal = fbt.reduce((sum, f) => sum + f.count, 0);

    const checksSent = approved.filter((e) => e.status === "donation approved");
    const totalSent = checksSent.reduce((sum, e: any) => sum + (Number(e.check_amount) || 0), 0);
    // Test-drive stats (D4URX-2767, D4URX-2768) count events with a donation requested or approved.
    // valid_post_count is saved when the donation is requested (checks.routes donation_approval), so
    // these all have one; an event somehow without it is left out rather than counted as 0.
    const donationEvents = approved.filter((e: any) => [ "donation requested", "donation approved" ].includes(e.status)
      && e.valid_post_count !== null && e.valid_post_count !== undefined);
    const validDrives = donationEvents.map((e: any) => Number(e.valid_post_count) || 0);
    // capped per event, then averaged: the drives that count toward the donation
    const eligibleDrives = validDrives.map((n) => Math.min(n, MAX_DRIVES_PER_EVENT));
    const average = (values: number[]) => (values.length ? values.reduce((a, b) => a + b, 0) / values.length : null);

    const byDealer = new Map<number, number>();
    approved.forEach((e) => byDealer.set(e.dealer_id, (byDealer.get(e.dealer_id) || 0) + 1));
    const multiEventDealers = Array.from(byDealer.values()).filter((n) => n >= 2);

    return {
      requests: {
        total: all.length,
        approved: approved.length,
        new: all.filter((e) => e.status === "new").length,
        ineligible: all.filter((e) => e.status === "ineligible").length,
        cancelled: all.filter((e) => e.status === "cancelled").length,
      },
      firstTime: { requests: firstTime.length, approved: firstTime.filter(isApproved).length },
      hispanic: { requests: hispanic.length, approved: hispanic.filter(isApproved).length },
      approvedCount: approved.length,
      orgs: {
        school: approved.filter((e) => e.organization_type === "school").length,
        community: approved.filter((e) => e.organization_type === "community_org").length,
        unclassified: approved.filter((e) => !e.organization_type).length,
      },
      // Every subcategory row, including zeros, grouped under its organization type
      subcategories: ORGANIZATION_TYPES.flatMap((type) => ORGANIZATION_SUBCATEGORIES[type.value].map((sub) => ({
        type: type.label,
        subcategory: sub.label,
        count: approved.filter((e) => e.organization_type === type.value && e.organization_subcategory === sub.value).length,
      }))),
      // approved before the subcategory existed (incl. staging events approved with only a type)
      subcategoryUnclassified: approved.filter((e) => !e.organization_subcategory).length,
      funding,
      fbt,
      fbtTotal,
      donations: {
        checksSent: checksSent.length,
        totalSent,
        drivesCounted: donationEvents.length,
        avgValidDrives: average(validDrives),
        avgEligibleDrives: average(eligibleDrives),
        // D4URX-2768: donation requested/approved events with 200+ valid test-drives ($6,000 / $30).
        // valid_post_count is saved when the donation is requested, so both statuses have it.
        atMaxCount: validDrives.filter((n) => n >= MAX_DRIVES_PER_EVENT).length,
        avgDonation: checksSent.length ? totalSent / checksSent.length : 0,
      },
      multiEvent: {
        dealers: multiEventDealers.length,
        events: multiEventDealers.reduce((a, b) => a + b, 0),
      },
    };
  }, [events]);

  // ---- export: styled "Summary" sheet (src/utils/dataExportWorkbook.ts) ----
  // Page section title -> workbook blocks it exports
  const SECTION_BLOCKS: Record<string, ExportBlock[]> = {
    "Event Requests & Dealers": [ "requests" ],
    Organizations: [ "orgs" ],
    "Funding Purpose Categories": [ "funding" ],
    "FBT Partners": [ "fbt" ],
    Donations: [ "donations" ],
    Reports: [ "reports" ],
  };

  // The exporter reads fileName from its props, so set it first and save once it has rendered.
  const [pendingExport, setPendingExport] = useState<{ workbook: any; fileName: string } | null>(null);
  useEffect(() => {
    if (pendingExport && exportRef.current) {
      exportRef.current.save(pendingExport.workbook);
      setPendingExport(null);
    }
  }, [pendingExport]);

  const exportBlocks = (blocks: ExportBlock[], name: string) => {
    const s = stats;
    const workbook = buildDataExportWorkbook({
      seasonName,
      exportedBy: login.user?.credentials.display_name || "",
      exportedAt: new Date(),
      requests: s.requests,
      firstTime: s.firstTime,
      hispanic: s.hispanic,
      orgs: s.orgs,
      subcategories: s.subcategories,
      subcategoryUnclassified: s.subcategoryUnclassified,
      funding: s.funding,
      fbt: s.fbt,
      donations: {
        checksSent: s.donations.checksSent,
        totalSent: s.donations.totalSent,
        atMaxCount: s.donations.atMaxCount,
        avgValidDrives: s.donations.avgValidDrives,
        avgEligibleDrives: s.donations.avgEligibleDrives,
      },
      reapplications,
      multiEvent: s.multiEvent,
    }, blocks);
    setPendingExport({ workbook, fileName: `${name}_${seasonName}_${moment().format("YYYYMMDD-hhmma")}.xlsx` });
  };
  const exportSection = (title: string) =>
    exportBlocks(SECTION_BLOCKS[title], `data_${title.toLowerCase().replace(/[^a-z0-9]+/g, "_")}`);
  const exportAll = () => exportBlocks([ "requests", "orgs", "funding", "fbt", "donations", "reports" ], "data_export");

  const SectionHeader = ({ title, children }: { title: string; children?: React.ReactNode }) => (
    <div className="data-section-header">
      <h3>{title}{children}</h3>
      <button className="k-button k-button-md k-rounded-md" onClick={() => exportSection(title)} disabled={loading}>
        Export section
      </button>
    </div>
  );

  const StatCard = ({ title, rows, footnote }: { title: string; rows: [string, number][]; footnote?: string }) => (
    <div className="data-card">
      <h4>{title}</h4>
      {rows.map(([ label, value ]) => (
        <div className="data-row" key={label}><span>{label}:</span><strong>{value}</strong></div>
      ))}
      {footnote && <div className="data-footnote">{footnote}</div>}
    </div>
  );

  // Horizontal 100% bar. Each segment carries a hover title; the legend above names every
  // segment with its count, so identity never depends on color alone.
  const SplitBar = ({ title, segments }: { title: string; segments: { label: string; count: number; color: string }[] }) => {
    const total = segments.reduce((sum, seg) => sum + seg.count, 0);
    const visible = segments.filter((seg) => seg.count > 0);
    return (
      <div className="data-split">
        <h4>{title}</h4>
        <div className="data-subtle">Approved Events ({total})</div>
        <div className="data-legend">
          {segments.map((seg) => (
            <span key={seg.label}>
              <i style={{ background: seg.color }} />{seg.label} <strong>({seg.count}{total ? ` · ${pct(seg.count, total)}` : ""})</strong>
            </span>
          ))}
        </div>
        <div className="data-splitbar" role="img" aria-label={`${title}: ${segments.map((s) => `${s.label} ${s.count}`).join(", ")}`}>
          {total === 0 && <div className="data-splitbar-empty">No approved events</div>}
          {visible.map((seg) => (
            <div
              key={seg.label}
              className="data-split-seg"
              title={`${seg.label}: ${seg.count} (${pct(seg.count, total)})`}
              style={{ flex: `${seg.count} 1 0`, background: seg.color, color: seg.color === COLORS.unclassified ? "#333" : "#fff" }}
            >
              {seg.count / total >= 0.08 ? pct(seg.count, total) : ""}
            </div>
          ))}
        </div>
        {total > 0 && (
          <div className="data-footnote">Each percentage is that group's share of the {total} approved events.</div>
        )}
      </div>
    );
  };

  const s = stats;
  const unclassifiedNote = s.orgs.unclassified > 0
    ? `${s.orgs.unclassified} approved event${s.orgs.unclassified === 1 ? "" : "s"} predate the approval classification and show as not classified.`
    : "";

  const drivesCountedNote = ` Across ${s.donations.drivesCounted} event${s.donations.drivesCounted === 1 ? "" : "s"}.`;

  return (
    <div id="Dashboard" className="dashboard-page main-content">
      <style>{`
        .data-page { text-align: left; }
        .data-head { display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 12px; margin-bottom: 12px; }
        .data-head h2 { margin: 0; }
        .data-head .data-subtle { margin-top: 4px; }
        .data-head-actions { display: flex; align-items: center; gap: 10px; }
        .data-head-actions select { padding: 6px 10px; border: 1px solid #ccc; border-radius: 4px; }
        .data-section-header { display: flex; justify-content: space-between; align-items: center; margin: 22px 0 10px; }
        .data-section-header h3 { margin: 0; font-weight: 700; font-size: 17px; }
        .data-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 16px; }
        .data-card { background: #f5f5f5; border-radius: 5px; padding: 12px 14px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
        .data-card h4 { margin: 0 0 8px; font-size: 15px; color: #444; }
        .data-row { display: flex; justify-content: space-between; font-size: 15px; line-height: 1.6; }
        .data-footnote, .data-subtle { font-size: 12px; color: #666; }
        .data-footnote { margin-top: 10px; }
        .data-panel { background: #f5f5f5; border-radius: 5px; padding: 14px; }
        .data-split-wrap { display: grid; grid-template-columns: repeat(auto-fit, minmax(360px, 1fr)); gap: 40px; }
        .data-split h4 { margin: 0; font-size: 15px; }
        .data-legend { display: flex; flex-wrap: wrap; gap: 4px 16px; margin: 8px 0; font-size: 13px; }
        .data-legend i { display: inline-block; width: 12px; height: 12px; border-radius: 2px; margin-right: 6px; vertical-align: -1px; }
        .data-splitbar { display: flex; gap: 2px; height: 22px; background: ${COLORS.track}; border-radius: 4px; overflow: hidden; }
        .data-split-seg { min-width: 4px; font-size: 11px; font-weight: 600; line-height: 22px; text-align: center; white-space: nowrap; overflow: hidden; }
        .data-splitbar-empty { font-size: 12px; color: #666; line-height: 22px; padding-left: 8px; }
        .data-table { width: 100%; border-collapse: collapse; background: #fff; border: 1px solid #e3e3e3; }
        .data-table th, .data-table td { padding: 9px 12px; border-bottom: 1px solid #eee; font-size: 14px; text-align: left; vertical-align: middle; }
        .data-table th { background: #f5f5f5; font-weight: 600; }
        .data-table tr.data-subhead td { font-weight: 700; background: #fafafa; }
        .data-table .num { text-align: right; white-space: nowrap; }
        .data-table tfoot td { font-weight: 700; background: #f5f5f5; }
        .data-track { height: 10px; background: ${COLORS.track}; border-radius: 4px; }
        .data-fill { height: 10px; background: ${COLORS.bar}; border-radius: 4px; }
        .data-rules { font-size: 13px; margin-bottom: 10px; display: flex; flex-wrap: wrap; gap: 4px 20px; }
        .data-tiles { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 16px; }
        .data-tile { background: #fff; border-radius: 4px; padding: 12px 14px; }
        .data-tile .label { font-size: 13px; color: #444; }
        .data-tile .hero { font-size: 28px; font-weight: 700; margin: 4px 0; }
      `}</style>

      {/* Single exporter; each button hands it a full workbook definition */}
      <ExcelExport ref={exportRef} fileName={pendingExport?.fileName || "data.xlsx"} />

      <div className="card-container data-page">
        <div className="data-head">
          <div>
            <h2 className="card-title">{login.user?.credentials.display_name}: Data</h2>
            <div className="data-subtle">
              {seasonName} program{updatedAt ? ` · Updated ${moment(updatedAt).format("ddd, MMM D YYYY, h:mm A")}` : ""}
            </div>
          </div>
          <div className="data-head-actions">
            <label htmlFor="data-season" className="data-subtle">Program year</label>
            <select id="data-season" value={seasonId} onChange={(e) => setSeasonId(Number(e.target.value))}>
              {(seasons.length ? seasons : [ ui.currentSeason ]).map((season: any) => (
                <option key={season.season_id} value={season.season_id}>{season.season_name}</option>
              ))}
            </select>
            {/* Same shape as the "Export section" buttons, in the admin's blue */}
            <button
              className="k-button k-button-md k-rounded-md"
              style={{ background: "#1A6FE0", borderColor: "#1A6FE0", color: "#fff" }}
              onClick={exportAll}
              disabled={loading}
            >
              Export All Data
            </button>
          </div>
        </div>

        {error && <p style={{ color: "red" }}>{error}</p>}
        {loading && <p className="data-subtle">Loading…</p>}

        {!loading && !error && (
          <>
            <SectionHeader title="Event Requests & Dealers" />
            <div className="data-grid">
              <StatCard
                title="Event Requests"
                rows={[
                  [ "Total", s.requests.total ],
                  [ "Approved", s.requests.approved ],
                  [ "New", s.requests.new ],
                  [ "Ineligible", s.requests.ineligible ],
                  [ "Cancelled", s.requests.cancelled ],
                ]}
              />
              <StatCard
                title="First Time Dealers"
                rows={[ [ "Requests", s.firstTime.requests ], [ "Approved", s.firstTime.approved ] ]}
                footnote={`${pct(s.firstTime.approved, s.firstTime.requests)} approval rate`}
              />
              <StatCard
                title="Hispanic Dealers"
                rows={[ [ "Requests", s.hispanic.requests ], [ "Approved", s.hispanic.approved ] ]}
                footnote={`${pct(s.hispanic.approved, s.hispanic.requests)} approval rate`}
              />
            </div>

            <SectionHeader title="Organizations" />
            <div className="data-panel">
              <div className="data-split-wrap">
                <SplitBar
                  title="Schools/Youth Programs vs. Community Organizations"
                  segments={[
                    { label: "Schools/Youth Programs", count: s.orgs.school, color: COLORS.school },
                    { label: "Community Organizations", count: s.orgs.community, color: COLORS.community },
                    ...(s.orgs.unclassified ? [ { label: "Not classified yet", count: s.orgs.unclassified, color: COLORS.unclassified } ] : []),
                  ]}
                />
              </div>
              {unclassifiedNote && <div className="data-footnote">{unclassifiedNote}</div>}
            </div>

            <table className="data-table">
              <thead>
                <tr>
                  <th>Subcategory</th>
                  <th style={{ width: "35%" }}>Share of approved events ({s.approvedCount})</th>
                  <th className="num">Events</th>
                  <th className="num">%</th>
                </tr>
              </thead>
              <tbody>
                {s.subcategories.map(({ type, subcategory, count }, i) => [
                  // a subheader row before the first subcategory of each organization type
                  ...(i === 0 || s.subcategories[i - 1].type !== type
                    ? [ <tr key={`${type}-head`} className="data-subhead"><td colSpan={4}>{type}</td></tr> ]
                    : []),
                  <tr key={`${type}-${subcategory}`}>
                    <td>{subcategory}</td>
                    <td>
                      <div className="data-track" title={`${subcategory}: ${count} (${pct(count, s.approvedCount)})`}>
                        {count > 0 && <div className="data-fill" style={{ width: pct(count, s.approvedCount) }} />}
                      </div>
                    </td>
                    <td className="num"><strong>{count}</strong></td>
                    <td className="num">{pct(count, s.approvedCount)}</td>
                  </tr>,
                ])}
                {s.subcategoryUnclassified > 0 && (
                  <tr>
                    <td>Not classified yet</td>
                    <td />
                    <td className="num"><strong>{s.subcategoryUnclassified}</strong></td>
                    <td className="num">{pct(s.subcategoryUnclassified, s.approvedCount)}</td>
                  </tr>
                )}
              </tbody>
            </table>

            <SectionHeader title="Funding Purpose Categories" />
            <table className="data-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th style={{ width: "35%" }}>Share of approved events ({s.approvedCount})</th>
                  <th className="num">Events</th>
                  <th className="num">%</th>
                </tr>
              </thead>
              <tbody>
                {s.funding.map(({ category, count }) => (
                  <tr key={category}>
                    <td>{category}</td>
                    <td>
                      <div className="data-track" title={`${category}: ${count} (${pct(count, s.approvedCount)})`}>
                        {count > 0 && <div className="data-fill" style={{ width: pct(count, s.approvedCount) }} />}
                      </div>
                    </td>
                    <td className="num"><strong>{count}</strong></td>
                    <td className="num">{pct(count, s.approvedCount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>Total</td>
                  <td />
                  <td className="num">{s.approvedCount}</td>
                  <td className="num">{s.approvedCount ? "100%" : "0.0%"}</td>
                </tr>
              </tfoot>
            </table>

            <SectionHeader title="FBT Partners" />
            <table className="data-table">
              <thead>
                <tr>
                  <th>Partner</th>
                  <th style={{ width: "35%" }}>Share of approved events ({s.approvedCount})</th>
                  <th className="num">Events</th>
                  <th className="num">%</th>
                </tr>
              </thead>
              <tbody>
                {s.fbt.map(({ partner, count }) => (
                  <tr key={partner}>
                    <td>{partner}</td>
                    <td>
                      <div className="data-track" title={`${partner}: ${count} (${pct(count, s.approvedCount)})`}>
                        {count > 0 && <div className="data-fill" style={{ width: pct(count, s.approvedCount) }} />}
                      </div>
                    </td>
                    <td className="num"><strong>{count}</strong></td>
                    <td className="num">{pct(count, s.approvedCount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>Total FBT partner events</td>
                  <td />
                  <td className="num">{s.fbtTotal}</td>
                  <td className="num">{pct(s.fbtTotal, s.approvedCount)}</td>
                </tr>
              </tfoot>
            </table>
            <div className="data-footnote">
              {s.fbtTotal} of the {s.approvedCount} approved events have an FBT partner designated as their primary beneficiary.
            </div>

            <SectionHeader title="Donations" />
            <div className="data-panel">
              <div className="data-rules">
                <span><strong>{money(DONATION_PER_DRIVE)}</strong> per valid test-drive</span>
                <span><strong>{money(MAX_DONATION_PER_EVENT)}</strong> max donation per event</span>
                <span><strong>{MAX_DRIVES_PER_EVENT}</strong> max eligible test-drives per event</span>
              </div>
              <div className="data-tiles">
                <div className="data-tile">
                  <div className="label">Event donations approved (checks sent)</div>
                  <div className="hero">{s.donations.checksSent}</div>
                  <div className="data-subtle">of {s.approvedCount} approved events</div>
                </div>
                <div className="data-tile">
                  <div className="label">Total donation amount sent</div>
                  <div className="hero">{money(s.donations.totalSent)}</div>
                  <div className="data-subtle">all events, checks sent</div>
                </div>
                <div className="data-tile">
                  <div className="label">Avg Valid Test-Drives per Event</div>
                  <div className="hero">{oneDecimal(s.donations.avgValidDrives)}</div>
                  <div className="data-subtle">
                    Average valid test drives per event with a donation requested or approved. Not capped.
                    {drivesCountedNote}
                  </div>
                </div>
                <div className="data-tile">
                  <div className="label">Avg Donation-Eligible Test-Drives per Event</div>
                  <div className="hero">{oneDecimal(s.donations.avgEligibleDrives)}</div>
                  <div className="data-subtle">
                    Average test drives per event that count toward the donation, capped at {MAX_DRIVES_PER_EVENT} per event ({money(MAX_DONATION_PER_EVENT)} max).
                    {drivesCountedNote}
                  </div>
                </div>
                <div className="data-tile">
                  <div className="label">Avg. donation per event</div>
                  <div className="hero">{money(s.donations.avgDonation)}</div>
                  <div className="data-subtle">{money(s.donations.totalSent)} ÷ {s.donations.checksSent} events</div>
                </div>
                <div className="data-tile">
                  <div className="label">Events at Max Donation</div>
                  <div className="hero">{s.donations.atMaxCount}</div>
                  <div className="data-subtle">
                    Number of events that reached the {money(MAX_DONATION_PER_EVENT)} max donation ({MAX_DRIVES_PER_EVENT}+ valid test drives).
                  </div>
                </div>
              </div>
            </div>

            <SectionHeader title="Reports" />
            <div className="data-grid">
              {reapplications ? (
                <StatCard
                  title="Declined Dealers Who Reapplied"
                  rows={[
                    [ "Reapplied", reapplications.reapplied ],
                    [ "Approved on reapply", reapplications.approved ],
                    [ "Declined again", reapplications.declined_again ],
                    [ "Pending", reapplications.pending ],
                  ]}
                  footnote="Dealers declined this season who sent a later request this season, by that request's status"
                />
              ) : (
                <div className="data-card"><h4>Declined Dealers Who Reapplied</h4><p className="data-subtle">Could not load.</p></div>
              )}
              <StatCard
                title="Dealers Hosting 2+ Events"
                rows={[ [ "Dealers", s.multiEvent.dealers ], [ "Events", s.multiEvent.events ] ]}
                footnote={`${pct(s.multiEvent.events, s.approvedCount)} of approved events`}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default observer(DataScreen);
